// The trace strip above the transport: the numbers behind the picture, read from the sims on
// screen. Blood flow and stroke: flow and pressure over the cardiac cycle for the selected vessel,
// and tissue volumes over the hours. Seizure: a stacked EEG or SEEG strip. Drugs: receptor
// occupancy against concentration (or over time, for a PK run). Neurons: membrane voltage at the
// soma and at the dendritic sites where the run's NMDA spikes fired, with its spikes and stimuli.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAtlas, type SimSlot } from '../app/store.ts';
import { scenarioById } from '../scenarios/index.ts';
import { drawnOverlays, liveActive, slotsOf, stillView, timelineOf } from '../app/scene.ts';
import { cfdActive } from './cfd.ts';
import { liveSolver, packHas } from '../app/live.ts';
import { seriesAt, structureVessels, type PointSeries } from '../data/sims/live.ts';
import { openedSlot } from '../app/sims.ts';
import { Chart, MONO, textWidth, type ChartModel, type Lane, type Series } from '../ui/Chart.tsx';
import { extent, formatTick, niceTicks, padded, valueAt } from '../ui/chart.ts';
import { IconButton, Segmented } from '../ui/primitives.tsx';
import { useLoaded } from '../ui/hooks.ts';
import { gradientCss, RAMPS, SERIES, TISSUE_FATE, TRACE } from '../ui/scales.ts';
import { formatTime } from './time.ts';
import type { HemoSim, HemoSegment, GrowthTable } from '../data/sims/hemo.ts';
import { channelGroups, type DynamicsSim, type SensorKind } from '../data/sims/dynamics.ts';
import { doseLabel, occupancy, type BindingCurve, type PharmaSim, type PkCourse, type PkSeries } from '../data/sims/pharma.ts';
import { courseDose } from '../data/sims/dose.ts';
import { usePharmaCourse } from './usePharmaCourse.ts';
import type { CellsSim } from '../data/sims/cells.ts';
import { isCircuitKind, loadCircuit, rasterGutter, rateTop } from '../data/sims/circuit.ts';
import { formatCount } from '../data/measures.ts';
import { frameTimes, duration } from '../data/sims/time.ts';
import { formatValue } from '../ui/chart.ts';
import { isPatientComparison } from '../data/sims/comparison.ts';
import { PatientTraces } from './PatientTraces.tsx';
import { VisualFieldStrip } from './VisualField.tsx';
import { sensorScale } from './sensorScale.ts';
import { isEmptyGrowth } from './pcom.ts';
import { LifetimeTraces, ThicknessTraces } from './Lifetime.tsx';
import { thicknessView } from './lifetime.ts';

const HEIGHT = 168;

/** The cursor for charts whose x is scene time (stable, so a chart doesn't repaint on every render). */
const sceneTime = (t: number) => t;

// useLoaded lives in ui/hooks.ts (the clot panel and Methods use it too); the patient views import it from here.
export { useLoaded };

const msFormat = (x: number) => `${Math.round(x * 1000)}`;
const secFormat = (x: number) => (Math.abs(x) < 60 ? `${formatValue(x, 3).replace(/\.0+$/, '')} s` : formatTime(x, 's'));

/** `wrap`: a header too crowded for one row (Drugs: six receptors and a key) moves its controls to a second row rather than cutting the title. */
export function Frame({
  title,
  sub,
  controls,
  keyBar,
  wrap = false,
  onTitle,
  children,
}: {
  title: string;
  sub?: string;
  controls?: ReactNode;
  keyBar?: ReactNode;
  wrap?: boolean;
  /** The title selects what the traces are of (Neurons' simulated cell, which the viewport doesn't pick). */
  onTitle?: () => void;
  children: ReactNode;
}) {
  const open = useAtlas((s) => s.tracesOpen);
  const setOpen = useAtlas((s) => s.setTracesOpen);
  return (
    <section className={`traces ${open ? '' : 'closed'}${wrap ? ' wrap' : ''}`} aria-label="Traces" data-inset="bottom">
      <header className="traces-head">
        <div className="traces-title">
          <h3>
            {onTitle ? (
              <button type="button" className="traces-title-btn" title="Select it" onClick={onTitle}>
                {title}
              </button>
            ) : (
              title
            )}
          </h3>
          {sub ? <span className="traces-sub">{sub}</span> : null}
        </div>
        <div className="traces-controls">
          {open ? controls : null}
          {open ? keyBar : null}
          <IconButton size="sm" icon={open ? ChevronDown : ChevronUp} label={open ? 'Hide traces' : 'Show traces'} onClick={() => setOpen(!open)} />
        </div>
      </header>
      {open ? <div className="traces-body">{children}</div> : null}
    </section>
  );
}

/** A small colour key for what the viewport colours by: ramp, range and unit. */
function Key({ ramp, range, label, unit }: { ramp: keyof typeof RAMPS; range: [number, number]; label: string; unit: string }) {
  return (
    <span className="traces-key" title={`${label} in 3D`}>
      <span className="traces-key-label">{label}</span>
      <span className="num">{formatValue(range[0])}</span>
      <span className="traces-key-bar" style={{ background: gradientCss(RAMPS[ramp]) }} />
      <span className="num">{`${formatValue(range[1])}${unit ? ` ${unit}` : ''}`}</span>
    </span>
  );
}

/** A categorical key: one swatch per colour in the chart (and in 3D, where they match). */
export function SwatchKey({ items }: { items: { label: string; colour: string }[] }) {
  return (
    <span className="traces-key">
      {items.map((i) => (
        <span key={i.label} className="traces-swatch">
          <span className="swatch" style={{ background: i.colour }} />
          {i.label}
        </span>
      ))}
    </span>
  );
}

export function OverlayKey({ slot }: { slot: string }) {
  const ov = useAtlas((s) => drawnOverlays(s).find((o) => o.id.startsWith(`${slot}:`)));
  if (!ov) return null;
  const label = ov.quantity === 'velocity' ? 'Speed' : ov.quantity.charAt(0).toUpperCase() + ov.quantity.slice(1);
  // Units like "occupancy multiplied by target-wise relative PET density" belong in Methods, not a key.
  const unit = ov.units.length <= 10 && !/_/.test(ov.units) ? ov.units : '';
  return <Key ramp={ov.ramp} range={ov.range} label={label} unit={unit} />;
}

// Blood flow: one vessel over one cycle.

/** A periodic series closed at t = period with its first value, so a cycle draws end to end. */
function cycle(times: Float64Array, ys: Float32Array, period: number, periodic: boolean): { x: Float64Array; y: Float32Array } {
  if (!periodic || !times.length || times[times.length - 1]! >= period - 1e-9) return { x: times, y: ys };
  const x = new Float64Array(times.length + 1);
  const y = new Float32Array(ys.length + 1);
  x.set(times);
  y.set(ys);
  x[times.length] = times[0]! + period;
  y[ys.length] = ys[0]!;
  return { x, y };
}

function pickSegment(sim: HemoSim, wanted: string | undefined, selected: string | null, fallback: string | undefined): { seg: HemoSegment | null; options: HemoSegment[] } {
  const bySel = selected ? sim.segmentsFor(selected) : [];
  const options = bySel.length ? bySel : fallback ? sim.segmentsFor(fallback) : [];
  const seg = (wanted ? (options.find((s) => s.id === wanted) ?? sim.segment(wanted)) : undefined) ?? options[0] ?? sim.segments[0] ?? null;
  return { seg, options: options.length > 1 ? options : [] };
}

/** The flow trace: the live solver's while live mode is on, else the run's. */
function VesselTraces({ slot, follows, extraControls }: { slot?: SimSlot; follows: boolean; extraControls?: ReactNode }) {
  const live = useAtlas(liveActive);
  if (live && liveSolver()) return <LiveVesselTraces follows={follows} extraControls={extraControls} />;
  return slot ? <RunVesselTraces slot={slot} follows={follows} extraControls={extraControls} /> : null;
}

/**
 * One vessel of the live solve over its cycle, against the same vessel in the intact network
 * (dashed). A clotted vessel shows both faces of the clot: no flow through it, and the pressure on
 * either side.
 */
function LiveVesselTraces({ follows, extraControls }: { follows: boolean; extraControls?: ReactNode }) {
  const solver = liveSolver()!;
  const result = useAtlas((s) => s.live.result);
  const overlay = useAtlas((s) => s.live.overlay);
  const selected = useAtlas((s) => s.selected);
  const wanted = useAtlas((s) => s.traceView.vessel);
  const fallback = useAtlas((s) => scenarioById(s.scenario).traceDefault);
  const catalogue = useAtlas((s) => s.catalogue);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const setTime = useAtlas((s) => s.setTime);
  const groups = useMemo(() => structureVessels(solver.vessels, packHas({ catalogue })), [solver, catalogue]);
  const known = (id: string | null | undefined) => (id && solver.vessels.some((v) => v.id === id) ? id : undefined);
  // An explicit pick, else the selected artery's first vessel, else the scene's default vessel.
  const vessel = known(wanted) ?? (selected ? (groups.get(selected)?.[0] ?? known(selected)) : undefined) ?? (fallback ? (groups.get(fallback)?.[0] ?? known(fallback)) : undefined) ?? solver.vessels[0]!.id;
  const seg = result?.segments.find((g) => g.id === vessel);
  const model = useMemo((): ChartModel | null => {
    if (!result || !seg?.count) return null;
    const period = result.axis.period;
    const times = frameTimes(result.axis);
    const changed = result.clots.length > 0 || result.absent.length > 0;
    const base = changed ? seriesAt(solver, solver.baseline, vessel) : null;
    const points = (seg.clot ? [['before the clot', seriesAt(solver, result, vessel, seg.clot.s, 'proximal')], ['beyond it', seriesAt(solver, result, vessel, seg.clot.s, 'distal')]] : [['', seriesAt(solver, result, vessel)]]) as [string, PointSeries | null][];
    const lanes: Lane[] = [];
    const add = (label: string, unit: string, pick: (p: PointSeries) => Float32Array, colours: string[]) => {
      const series: Series[] = [];
      const ys: ArrayLike<number>[] = [];
      if (base) {
        const b = cycle(frameTimes(solver.baseline.axis), pick(base), solver.baseline.axis.period, true);
        series.push({ label: `${label}, no clot`, colour: '#a7b0be', x: b.x, y: b.y, style: 'dash', width: 1.25, alpha: 0.75, unit });
        ys.push(b.y);
      }
      points.forEach(([where, p], i) => {
        if (!p) return;
        const own = cycle(times, pick(p), period, true);
        series.push({ label: where ? `${label}, ${where}` : label, colour: colours[i]!, x: own.x, y: own.y, width: 1.75, unit });
        ys.push(own.y);
      });
      lanes.push({ label, unit, y: padded(extent(ys)), ticks: 'auto', series });
    };
    add('Flow', solver.units.flow, (p) => p.flow, [SERIES[0]!, SERIES[2]!]);
    add('Pressure', solver.units.pressure, (p) => p.pressure, [SERIES[1]!, SERIES[3]!]);
    return { x: { domain: [0, period], format: msFormat }, lanes, gutter: 46 };
  }, [result, seg, vessel, solver]);
  const period = result?.axis.period ?? 1;
  const cycleCursor = useMemo(() => (t: number) => ((t % period) + period) % period, [period]);
  const name = solver.vessels.find((v) => v.id === vessel)?.name ?? vessel;
  const stateOf = (id: string) => {
    const g = result?.segments.find((x) => x.id === id);
    return g?.clot ? ' (clot)' : g && !g.count ? ' (out)' : '';
  };
  const where = seg?.clot ? `clot at ${Math.round(seg.clot.s * 100)} %` : seg && !seg.count ? 'out of the network' : 'mid-vessel';
  const controls = (
    <>
      {extraControls}
      <select className="select traces-select" aria-label="Vessel" value={vessel} onChange={(e) => setTraceView({ vessel: e.target.value })}>
        {solver.vessels.map((v) => (
          <option key={v.id} value={v.id}>{`${v.name}${stateOf(v.id)}`}</option>
        ))}
      </select>
    </>
  );
  const keyLabel = overlay ? `${overlay.times.length > 1 ? '' : 'Mean '}${overlay.quantity}` : '';
  return (
    <Frame title={name} sub={`live · cardiac cycle, ms · ${where}`} controls={controls} keyBar={overlay ? <Key ramp={overlay.ramp} range={overlay.range} label={keyLabel.charAt(0).toUpperCase() + keyLabel.slice(1)} unit={overlay.units} /> : null}>
      {model ? (
        <Chart label={`Live flow and pressure in ${name} over one cardiac cycle`} model={model} height={HEIGHT} cursor={follows ? cycleCursor : undefined} onSeek={follows ? (x) => setTime(x) : undefined} />
      ) : (
        <div className="traces-empty">{seg && !seg.count ? 'Out of the network: the collaterals are off.' : 'Solving…'}</div>
      )}
    </Frame>
  );
}

function RunVesselTraces({ slot, follows, extraControls }: { slot: SimSlot; follows: boolean; extraControls?: ReactNode }) {
  const o = openedSlot(slot.slot);
  const ref = openedSlot('reference');
  const sim = o?.format === 'hemo' ? o.sim : null;
  const refSim = ref?.format === 'hemo' && ref.sim.isNetwork ? ref.sim : null;
  const selected = useAtlas((s) => s.selected);
  const wanted = useAtlas((s) => s.traceView.segment);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const fallback = useAtlas((s) => scenarioById(s.scenario).traceDefault);
  const refLabel = useAtlas((s) => slotsOf(s).find((x) => x.slot === 'reference')?.label ?? 'reference');
  const setTime = useAtlas((s) => s.setTime);
  // Beside the 3D flow, the panel still plots the 1D run (the CFD records flow and pressure only at its cut faces), so it says so.
  const besideCfd = useAtlas((s) => cfdActive(s) && slot.slot === 'main');
  const modelLabel = useAtlas((s) => `1D model (${s.choices.model ?? 'v5'})`);
  const data = useLoaded(
    () => (sim ? Promise.all([sim.quantities.includes('flow') ? sim.series('flow') : null, sim.quantities.includes('pressure') ? sim.series('pressure') : null]) : null),
    [sim],
  );
  const refData = useLoaded(() => (refSim ? Promise.all([refSim.quantities.includes('flow') ? refSim.series('flow') : null, refSim.quantities.includes('pressure') ? refSim.series('pressure') : null]) : null), [refSim]);
  const { seg, options } = sim ? pickSegment(sim, wanted, selected, fallback) : { seg: null, options: [] };
  const model = useMemo((): ChartModel | null => {
    if (!sim || !seg || !data.value) return null;
    const [flow, pressure] = data.value;
    const period = duration(sim.axis);
    const times = frameTimes(sim.axis);
    const refSeg = refSim ? (refSim.segment(seg.id) ?? (seg.structure ? refSim.segmentsFor(seg.structure)[0] : undefined)) : undefined;
    const lanes: Lane[] = [];
    const add = (label: string, unit: string, arr: Float32Array | null, refArr: Float32Array | null | undefined, colour: string) => {
      if (!arr) return;
      const own = cycle(times, sim.segmentSeries(arr, seg, 0.5), period, sim.axis.periodic);
      const series: Series[] = [];
      let ys = [own.y as ArrayLike<number>];
      if (refSim && refSeg && refArr) {
        const r = cycle(frameTimes(refSim.axis), refSim.segmentSeries(refArr, refSeg, 0.5), duration(refSim.axis), refSim.axis.periodic);
        series.push({ label: `${label}, ${refLabel.toLowerCase()}`, colour: '#a7b0be', x: r.x, y: r.y, style: 'dash', width: 1.25, alpha: 0.75, unit });
        ys = [...ys, r.y];
      }
      series.push({ label, colour, x: own.x, y: own.y, width: 1.75, unit });
      lanes.push({ label, unit, y: padded(extent(ys)), ticks: 'auto', series });
    };
    add('Flow', sim.unit('flow'), flow, refData.value?.[0], SERIES[0]!);
    add('Pressure', sim.unit('pressure'), pressure, refData.value?.[1], SERIES[1]!);
    if (!lanes.length) return null;
    return { x: { domain: [0, period], format: msFormat }, lanes, gutter: 46 };
  }, [sim, seg, data.value, refSim, refData.value, refLabel]);
  const cyclePeriod = sim ? duration(sim.axis) : 1;
  const cycleCursor = useMemo(() => (t: number) => ((t % cyclePeriod) + cyclePeriod) % cyclePeriod, [cyclePeriod]);
  if (!sim) return null;
  const controls = (
    <>
      {extraControls}
      {/* A vessel of more segments than fit beside the title (the basilar's six) picks from a list, in order along it. */}
      {options.length > 3 ? (
        <select className="select traces-select" aria-label="Segment" value={seg?.id ?? ''} onChange={(e) => setTraceView({ segment: e.target.value })}>
          {[...options]
            .sort((a, b) => a.index - b.index)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name.replace(/^(Left|Right) /, '')}
              </option>
            ))}
        </select>
      ) : options.length ? (
        <Segmented size="sm" label="Segment" value={seg?.id ?? null} options={options.map((s) => ({ value: s.id, label: s.name.replace(/^(Left|Right) /, '') }))} onChange={(v) => setTraceView({ segment: v })} />
      ) : null}
    </>
  );
  return (
    <Frame title={seg ? seg.name : 'Vessels'} sub={`${besideCfd ? `${modelLabel} · ` : ''}${seg?.state === 'occluded' ? 'occluded · cardiac cycle, ms' : 'cardiac cycle, ms · mid-segment'}`} controls={controls} keyBar={slot.appOnly ? null : <OverlayKey slot={slot.slot} />}>
      {model ? (
        <Chart
          label={`Flow and pressure in ${seg?.name ?? 'the vessel'} over one cardiac cycle`}
          model={model}
          height={HEIGHT}
          cursor={follows ? cycleCursor : undefined}
          onSeek={follows ? (x) => setTime(x) : undefined}
        />
      ) : (
        <div className="traces-empty">{data.error ?? 'Loading…'}</div>
      )}
    </Frame>
  );
}

// Stroke: tissue volumes over the hours.

const GROWTH: { col: string; label: string; short: string; colour: string }[] = [
  { col: 'core_ml', label: 'Core', short: 'Core', colour: TISSUE_FATE.core! },
  { col: 'penumbra_ml', label: 'Penumbra', short: 'Penumbra', colour: TISSUE_FATE.penumbra! },
  { col: 'reperfused_saved_ml', label: 'Reperfused, saved', short: 'Reperfused', colour: TISSUE_FATE['reperfused-saved']! },
];

function growthModel(table: GrowthTable, end: number, events: { t: number; label: string }[]): ChartModel {
  const x = table.time;
  const layers = GROWTH.filter((g) => table.values[g.col]);
  const series: Series[] = [];
  let below: Float64Array = new Float64Array(x.length);
  for (const g of layers) {
    const own = table.values[g.col]!;
    const top = Float64Array.from(own, (v, i) => v + below[i]!);
    series.push({ label: g.label, colour: g.colour, x, y: top, base: below, style: 'area', alpha: 0.85, readoutY: own, unit: 'mL' });
    below = top;
  }
  const hi = extent([below])[1];
  return {
    x: { domain: [0, end], format: (t) => formatTime(t, 'h').replace(' h', ''), ticks: niceTicks(0, end / 3600, 6).map((h) => h * 3600) },
    lanes: [{ label: 'Tissue', unit: 'mL', y: [0, hi > 0 ? hi * 1.12 : 1], ticks: 'auto', series }],
    events: events.map((e) => ({ x: e.t, label: e.label })),
    gutter: 46,
  };
}

function StrokeTraces({ main }: { main: SimSlot }) {
  const o = openedSlot('main');
  const sim = o?.format === 'hemo' ? o.sim : null;
  const tl = useAtlas(timelineOf);
  const flowSlot = useAtlas((s) => slotsOf(s).find((x) => x.slot === 'flow'));
  const [tab, setTab] = useState<'tissue' | 'flow'>('tissue');
  const live = useAtlas(liveActive);
  // v5's runs are the same for every grade: the one it takes isn't a grade to name.
  const gradeFixed = useAtlas((s) => !!s.sims.fixed?.grade);
  // Moving the clot is about flow: the strip follows it there (the scene opens on tissue).
  const occlusion = useAtlas((s) => s.stroke.occlusion?.vessel);
  const opened = useRef(occlusion);
  useEffect(() => {
    if (occlusion !== opened.current) setTab('flow');
  }, [occlusion]);
  const table = useLoaded(() => (sim ? sim.growth() : null), [sim]);
  const setTime = useAtlas((s) => s.setTime);
  const model = useMemo(() => (table.value && tl ? growthModel(table.value, tl.duration, tl.events ?? []) : null), [table.value, tl]);
  // A run with no tissue state ever above zero (v5's traced-PCom pair) has nothing to chart; say so rather than draw a 0–1 axis.
  const empty = !!table.value && isEmptyGrowth(table.value);
  const tabs = flowSlot || live ? (
    <Segmented
      size="sm"
      label="Trace"
      value={tab}
      options={[
        { value: 'tissue', label: 'Tissue' },
        { value: 'flow', label: 'Flow' },
      ]}
      onChange={setTab}
    />
  ) : null;
  if (tab === 'flow' && (flowSlot || live)) return <VesselTraces {...(flowSlot ? { slot: flowSlot } : {})} follows={false} extraControls={tabs} />;
  const grade = !gradeFixed && typeof main.header.scenario?.collateral_grade === 'string' ? `${main.header.scenario.collateral_grade} collaterals` : null;
  return (
    <Frame
      title={main.header.scenario?.label ?? 'Tissue fate'}
      sub={['tissue volumes, mL', grade].filter(Boolean).join(' · ')}
      controls={tabs}
      keyBar={table.value ? <SwatchKey items={GROWTH.filter((g) => table.value!.values[g.col]).map((g) => ({ label: g.short, colour: g.colour }))} /> : null}
    >
      {model && !empty ? <Chart label="Core, penumbra and reperfused tissue volumes over time" model={model} height={HEIGHT} cursor={sceneTime} onSeek={(x) => setTime(x)} /> : <div className="traces-empty">{empty ? 'No ischaemic tissue in this run: nothing to chart' : table.error ?? (sim ? 'Loading…' : '')}</div>}
    </Frame>
  );
}

// Seizure: a stacked strip of sensors.

const WINDOW_S = 10;
const EEG_HEIGHT = HEIGHT + 24;
/** The chart's padding above and below a strip's lanes (Chart's PAD), for a lane's height on screen. */
const STRIP_PAD = 34;

const SIDE: Record<string, string> = { L: 'Left', R: 'Right' };
const LOBE: Record<string, string> = { T: 'temporal', F: 'frontal', P: 'parietal', O: 'occipital', C: 'central', H: 'hippocampal', A: 'amygdala', I: 'insular' };

/** An SEEG electrode's name from its label, where the label follows side + lobe (LT → Left temporal). */
export function groupName(kind: SensorKind, label: string): string {
  if (kind !== 'seeg') return label;
  const m = /^([LR])([A-Z])$/.exec(label);
  return m && SIDE[m[1]!] && LOBE[m[2]!] ? `${SIDE[m[1]!]} ${LOBE[m[2]!]}` : label;
}

function EegTraces() {
  const o = openedSlot('main');
  const sim = o?.format === 'dynamics' ? (o.sim as DynamicsSim) : null;
  const view = useAtlas((s) => s.traceView);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const setTime = useAtlas((s) => s.setTime);
  const kinds = (['seeg', 'eeg'] as SensorKind[]).filter((k) => sim?.has(k) && sim.channels(k).length);
  const kind: SensorKind | undefined = view.montage && kinds.includes(view.montage) ? view.montage : kinds[0];
  const sensors = useLoaded(() => (sim && kind ? sim.sensors(kind) : null), [sim, kind]);
  const groups = useMemo(() => (sim && kind ? channelGroups(kind, sim.channels(kind)) : []), [sim, kind]);
  const group = groups.find((g) => g.id === view.group) ?? groups[0];
  // One sensitivity for the group, as a clinical viewer keeps it: lanes keep their relative size.
  const scale = useMemo(() => {
    const sens = sensors.value;
    return sens && group ? sensorScale(sens.data, sens.channels.length, group.channels, sens.units === 'V' ? 1e6 : 1, (EEG_HEIGHT - STRIP_PAD) / group.channels.length) : null;
  }, [sensors.value, group]);
  const model = useMemo((): ChartModel | null => {
    const sens = sensors.value;
    if (!sens || !group || !scale) return null;
    const x = frameTimes(sens.axis);
    const { half, unit, bar } = scale;
    const lanes: Lane[] = scale.traces.map((y, k) => ({ label: sens.channels[group.channels[k]!], y: [-half, half], ticks: 'none', series: [{ label: sens.channels[group.channels[k]!]!, colour: '#cfe6ff', x, y, width: 1, unit }] }));
    return { x: { domain: [0, Math.min(WINDOW_S, duration(sens.axis))], format: secFormat }, lanes, scaleBar: { value: bar, label: `${formatTick(bar)} ${unit}` }, gutter: 46 };
  }, [sensors.value, group, scale]);
  const unit = scale?.unit ?? 'µV';
  const end = sensors.value ? duration(sensors.value.axis) : 0;
  const win = useMemo(() => (end > WINDOW_S ? (t: number): [number, number] => {
    const a = Math.min(end - WINDOW_S, Math.max(0, t - WINDOW_S * 0.8));
    return [a, a + WINDOW_S];
  } : undefined), [end]);
  if (!sim) return null;
  const controls = (
    <>
      {kinds.length > 1 ? <Segmented size="sm" label="Sensors" value={kind ?? null} options={kinds.map((k) => ({ value: k, label: k === 'eeg' ? 'Scalp EEG' : 'SEEG', hint: k === 'eeg' ? 'Scalp electrodes, 10–20' : 'Depth electrodes' }))} onChange={(v) => setTraceView({ montage: v, group: undefined })} /> : null}
      {groups.length > 1 ? <Segmented size="sm" label="Channels" value={group?.id ?? null} options={groups.map((g) => ({ value: g.id, label: g.label }))} onChange={(v) => setTraceView({ group: v })} /> : null}
    </>
  );
  return (
    <Frame title={kind === 'eeg' ? 'Scalp EEG' : 'SEEG'} sub={`${group && kind ? `${groupName(kind, group.label)} · ` : ''}${unit}, mean-centred`} controls={controls} keyBar={<OverlayKey slot="main" />}>
      {model ? <Chart label={`${kind === 'eeg' ? 'Scalp EEG' : 'SEEG'} traces, ${unit}, each about its mean`} model={model} height={EEG_HEIGHT} cursor={sceneTime} window={win} onSeek={(x) => setTime(x)} /> : <div className="traces-empty">{sensors.error ?? 'Loading…'}</div>}
    </Frame>
  );
}

// Drugs: receptor occupancy.

/** Receptor codes as pharmacology writes them: 5HT2a → 5-HT2A, A4B2 → α4β2, GABAa → GABA-A. */
export const TARGET_LABEL = (t: string) =>
  t
    .replace(/^5HT(\d)([a-z])$/, (_, n: string, sub: string) => `5-HT${n}${sub.toUpperCase()}`)
    .replace(/^5HT/, '5-HT')
    .replace(/^A4B2$/, 'α4β2')
    .replace(/^GABAa$/, 'GABA-A');

function logspace(lo: number, hi: number, n: number): Float64Array {
  const a = Math.log10(lo);
  const b = Math.log10(hi);
  return Float64Array.from({ length: n }, (_, i) => 10 ** (a + ((b - a) * i) / (n - 1)));
}

function bindingModel(curves: BindingCurve[], target: string, exp: { low: number; central: number; high: number } | null, reach: number[] = []): ChartModel | null {
  if (!curves.length) return null;
  const lo = Math.min(...curves.map((c) => c.kiNm.low), exp?.low ?? Infinity, ...reach.filter((v) => v > 0)) / 30;
  const hi = Math.max(...curves.map((c) => c.kiNm.high), exp?.high ?? 0, ...reach) * 30;
  const domain: [number, number] = [10 ** Math.floor(Math.log10(lo)), 10 ** Math.ceil(Math.log10(hi))];
  const x = logspace(domain[0], domain[1], 240);
  const series: Series[] = curves.map((c, i) => {
    const on = c.target === target;
    return { label: TARGET_LABEL(c.target), colour: SERIES[i % SERIES.length]!, x, y: Float32Array.from(x, (v) => occupancy(v, c.kiNm.central)), width: on ? 2.25 : 1.25, alpha: on ? 1 : 0.5 };
  });
  return {
    x: { domain, log: true, format: concFormat },
    lanes: [{ label: 'Occupancy', unit: 'fraction', y: [0, 1], ticks: [0, 0.25, 0.5, 0.75, 1], series }],
    // Each curve's half-occupancy point is its Ki.
    labels: curves.map((c, i) => ({ lane: 0, x: c.kiNm.central, y: 0.5, text: `${TARGET_LABEL(c.target)} ${concUnits(c.kiNm.central, (x) => formatValue(x))}`, colour: SERIES[i % SERIES.length]! })),
    ...(exp
      ? {
          bands: [{ x0: exp.low, x1: exp.high, colour: 'rgba(255,255,255,0.07)' }],
          markers: [{ x: exp.central, colour: 'rgba(238,242,247,0.85)', label: `Brain exposure ${formatValue(exp.central)} nM` }],
        }
      : {}),
    gutter: 46,
  };
}

const concUnits = (v: number, fmt: (x: number) => string) => (v >= 1e6 ? `${fmt(v / 1e6)} mM` : v >= 1000 ? `${fmt(v / 1000)} µM` : `${fmt(v)} nM`);
const concFormat = (v: number) => concUnits(v, formatTick);
const CONC_COLOUR: Record<string, string> = { brain_unbound_nm: '#5fe0c8', plasma_unbound_nm: '#7cc4ff', plasma_total_nm: '#a7b0be' };

/** A PK course over its clock: concentrations above, the chosen target's occupancy (with its bounds) below. */
function pkTimeModel(sim: PharmaSim, course: PkCourse, target: string): ChartModel {
  const x = frameTimes(sim.axis);
  const end = duration(sim.axis);
  const F = sim.axis.frames;
  const conc: Series[] = course.concentrations.map((c) => ({
    label: c.label,
    colour: CONC_COLOUR[c.quantity] ?? SERIES[0]!,
    x,
    y: c.values,
    style: c.quantity === 'plasma_unbound_nm' ? 'dash' : 'line',
    width: c.quantity === 'brain_unbound_nm' ? 2 : 1.25,
    alpha: c.quantity === 'plasma_total_nm' ? 0.7 : 1,
    unit: c.units,
  }));
  const lanes: Lane[] = [{ label: 'Concentration', unit: course.concentrations[0]?.units ?? 'nM', y: padded(extent(course.concentrations.map((c) => c.values))), ticks: 'auto', series: conc, weight: 1.15 }];
  // Each line named at its peak; lines that coincide (brain unbound = plasma unbound when Kp,uu is 1) share one name.
  const labels: NonNullable<ChartModel['labels']> = [];
  for (const c of course.concentrations) {
    let peak = 0;
    for (let i = 1; i < c.values.length; i++) if (c.values[i]! > c.values[peak]!) peak = i;
    const same = labels.find((l) => Math.abs(l.y - c.values[peak]!) <= 1e-4 * Math.abs(l.y) && Math.abs(l.x - x[peak]!) < 1e-6);
    if (same) same.text += ` · ${c.label}`;
    else labels.push({ lane: 0, x: x[peak]!, y: c.values[peak]!, text: c.label, colour: CONC_COLOUR[c.quantity] ?? SERIES[0]! });
  }
  const { targets, central, low, high } = course.occupancy;
  const k = targets.indexOf(target);
  if (k >= 0 && central) {
    const T = targets.length;
    const col = (a: Float32Array) => Float32Array.from({ length: F }, (_, f) => a[f * T + k]!);
    const occ: Series[] = [];
    if (low && high) occ.push({ label: 'Bounds', colour: 'rgba(95,224,200,0.22)', x, y: col(high), base: col(low), style: 'area', readout: false });
    occ.push({ label: `${TARGET_LABEL(target)} occupancy`, colour: '#5fe0c8', x, y: col(central), width: 2, unit: '' });
    lanes.push({ label: `${TARGET_LABEL(target)} occupancy${low && high ? ', band from the Ki range' : ''}`, y: [0, 1], ticks: [0, 0.5, 1], series: occ });
  }
  return { x: { domain: [0, end], format: (t) => formatTime(t, 'h').replace(' h', ''), ticks: niceTicks(0, end / 3600, 6).map((h) => h * 3600) }, lanes, labels, gutter: 46 };
}

function pkModel(series: PkSeries[], sim: PharmaSim): ChartModel | null {
  const x = frameTimes(sim.axis);
  const lanes: Lane[] = series.map((s) => {
    const C = s.channels.length;
    const ys = s.channels.map((_, c) => Float32Array.from({ length: sim.axis.frames }, (_, f) => s.values[f * C + c]!));
    return { label: s.quantity.replace(/_/g, ' ').replace(/ nm$/, ''), unit: s.units, y: padded(extent(ys)), ticks: 'auto', series: ys.map((y, c) => ({ label: s.channels[c]!, colour: SERIES[c % SERIES.length]!, x, y, unit: s.units })) };
  });
  const end = duration(sim.axis);
  return lanes.length ? { x: { domain: [0, end], format: (t) => formatTime(t, end < 7200 ? 'min' : 'h') }, lanes, gutter: 46 } : null;
}

function DrugTraces() {
  // The course at the Dose slider's dose, where it follows the model that rescales it.
  const { sim, pk, curves, course, factor } = usePharmaCourse();
  const wanted = useAtlas((s) => s.traceView.target);
  const setTraceView = useAtlas((s) => s.setTraceView);
  const setTime = useAtlas((s) => s.setTime);
  const tab = useAtlas((s) => s.traceView.drugView ?? 'time');
  const setTab = (v: 'time' | 'binding') => setTraceView({ drugView: v });
  const isCourse = !!course && (course.concentrations.length > 0 || !!course.occupancy.central);
  const target = sim ? (wanted && sim.targets.includes(wanted) ? wanted : (sim.primaryTarget ?? '')) : '';
  const brain = course?.concentrations[0] ?? null;
  const times = useMemo(() => (sim ? frameTimes(sim.axis) : null), [sim]);
  const view = isCourse ? tab : 'binding';
  const model = useMemo(() => {
    if (!sim) return null;
    if (isCourse && view === 'time') return pkTimeModel(sim, course!, target);
    if (curves.value?.length) return bindingModel(curves.value, target, sim.exposureNm, brain ? [extent([brain.values])[1]] : []);
    return pk.value?.length ? pkModel(pk.value, sim) : null;
  }, [sim, isCourse, view, course, target, curves.value, brain, pk.value]);
  // On the binding curves, a course's cursor is its brain concentration now: it slides along the curves as time plays.
  const cursor = useMemo(
    () => (isCourse && view === 'binding' && brain && times ? (t: number) => valueAt(times, brain.values, Math.min(Math.max(t, times[0]!), times[times.length - 1]!)) : isCourse ? (t: number) => t : undefined),
    [isCourse, view, brain, times],
  );
  // The title reads occupancy now: from the course at the current minute, else the header's prediction.
  const minute = useAtlas((s) => (isCourse ? Math.floor(s.time / 60) : 0));
  if (!sim) return null;
  let occ = sim.binding.find((b) => b.target === target)?.occupancy?.central;
  if (isCourse && course!.occupancy.central && times) {
    const k = course!.occupancy.targets.indexOf(target);
    const T = course!.occupancy.targets.length;
    if (k >= 0) occ = valueAt(times, Float32Array.from({ length: sim.axis.frames }, (_, f) => course!.occupancy.central![f * T + k]!), Math.min(minute * 60, duration(sim.axis)));
  }
  const excluded = (sim.header.excluded ?? []).map((e) => TARGET_LABEL(e.target));
  const d = courseDose(sim.header);
  const unit = d?.unit ? ` ${d.unit}` : '';
  const dose = d && factor !== 1 ? `${formatValue(d.mass * factor)}${unit}${d.route ? ` ${d.route}` : ''}, the course's ${formatValue(d.mass)}${unit} rescaled` : doseLabel(sim.header);
  const courseSub = dose ? `${dose} · hours after the dose` : `${sim.name.split(' · ').slice(1).join(' · ') || 'dose'} · hours after it`;
  const controls = (
    <>
      {isCourse ? (
        <Segmented
          size="sm"
          label="Trace"
          value={view}
          options={[
            { value: 'time', label: 'Over time' },
            { value: 'binding', label: 'Binding' },
          ]}
          onChange={setTab}
        />
      ) : null}
      {sim.targets.length ? <Segmented size="sm" label="Receptor" value={target} options={sim.targets.map((t) => ({ value: t, label: TARGET_LABEL(t) }))} onChange={(v) => setTraceView({ target: v })} /> : null}
    </>
  );
  return (
    <Frame
      title={`${isCourse ? sim.name.split(' · ')[0] : sim.name}${occ !== undefined && Number.isFinite(occ) ? ` · ${TARGET_LABEL(target)} ${Math.round(occ * 100)}% occupied` : ''}`}
      sub={isCourse ? (view === 'time' ? courseSub : 'binding · the line is brain concentration now') : `equilibrium binding${excluded.length ? ` · not modelled: ${excluded.join(', ')}` : ''}`}
      controls={controls}
      keyBar={<OverlayKey slot="main" />}
      wrap
    >
      {model ? (
        <Chart label={`${sim.name} receptor occupancy`} model={model} height={HEIGHT} cursor={cursor} onSeek={isCourse && view === 'time' ? (x) => setTime(x) : undefined} />
      ) : (
        <div className="traces-empty">{pk.error ?? curves.error ?? (pk.value || curves.value ? 'No binding data in this run' : 'Loading…')}</div>
      )}
    </Frame>
  );
}

// Neurons: membrane voltage as a stacked strip at one sensitivity, the soma on top and the dendritic
// sites below it, nearest first, so a backpropagating spike's fall with distance reads down the strip.

const MAX_CELLS = 6;
const THIN = '\u2009';

function CellTraces() {
  const o = openedSlot('main');
  const sim = o?.format === 'cells' ? (o.sim as CellsSim) : null;
  const setTime = useAtlas((s) => s.setTime);
  const select = useAtlas((s) => s.select);
  const traces = useLoaded(async () => {
    if (!sim) return null;
    // One cell: its soma and the dendritic sites its events name. A circuit: the first few somata.
    const one = sim.cells.length === 1 ? sim.cells[0]! : null;
    const cells = one ? [one] : sim.cells.slice(0, MAX_CELLS);
    const somata = await Promise.all(cells.map((c) => sim.somaCompartment(c)));
    const sites = (one && one.recording !== 'soma' ? await sim.dendriteSites(one) : []).sort((a, b) => (a.distanceUm ?? Infinity) - (b.distanceUm ?? Infinity));
    const ys = await sim.traces('v', [...somata, ...sites.map((x) => x.compartment)]);
    return { cells, sites, ys };
  }, [sim]);
  const markers = useMemo(() => (sim ? sim.markers() : []), [sim]);
  const model = useMemo((): ChartModel | null => {
    if (!sim || !traces.value) return null;
    const { cells, sites, ys } = traces.value;
    const x = frameTimes(sim.axis);
    const unit = sim.seriesInfo('v').units ?? 'mV';
    const single = cells.length === 1;
    const rows = [
      ...cells.map((c) => ({ label: single ? 'Soma' : (c.name ?? c.id), colour: TRACE.line })),
      ...sites.map((x) => ({ label: `${x.type === 'apical' ? 'Apical' : x.type === 'basal' ? 'Basal' : 'Dendrite'}${x.distanceUm === null ? '' : ` ${Math.round(x.distanceUm)}${THIN}µm`}`, colour: x.type === 'apical' ? SERIES[1]! : SERIES[0]! })),
    ];
    // One sensitivity for every row, as a clinical viewer keeps it: amplitudes compare down the strip.
    const y = padded(extent(ys), 0.04);
    const lanes: Lane[] = ys.map((v, i) => ({ label: rows[i]!.label, y, ticks: 'none', series: [{ label: rows[i]!.label, colour: rows[i]!.colour, x, y: v, width: 1.25, unit }] }));
    const bar = 50;
    return {
      x: { domain: [x[0] ?? 0, x[x.length - 1] ?? 1], format: msFormat },
      lanes,
      // Spikes in the marker colour; the stimuli and dendritic events that the transport also marks, dotted.
      events: markers.filter((m) => m.kind !== 'spike').map((m) => ({ x: m.t, label: m.label, rank: m.rank })),
      markers: markers.filter((m) => m.kind === 'spike').map((m) => ({ x: m.t, colour: TRACE.event })),
      scaleBar: { value: bar, label: `${bar}${THIN}${unit}` },
      gutter: Math.ceil(Math.max(46, ...rows.map((r) => textWidth(r.label, MONO) + 16))),
    };
  }, [sim, traces.value, markers]);
  if (!sim) return null;
  const single = sim.cells.length === 1 ? sim.cells[0]! : null;
  const sites = traces.value?.sites.length ?? 0;
  return (
    <Frame
      title={single ? (single.name ?? single.id) : `${sim.cells.length} cells`}
      {...(single ? { onTitle: () => select(`cell:${single.id}`) } : {})}
      sub={single ? `membrane voltage${sites ? `, soma and ${sites} dendritic site${sites > 1 ? 's' : ''}` : ''} · ms` : `soma voltage · ms${sim.cells.length > MAX_CELLS ? ` · first ${MAX_CELLS}` : ''}`}
    >
      {model ? (
        <Chart label={single && sites ? 'Membrane voltage at the soma and the dendritic sites' : 'Soma voltage'} model={model} height={model.lanes.length > 4 ? HEIGHT + 40 : HEIGHT} cursor={sceneTime} onSeek={(x) => setTime(x)} />
      ) : (
        <div className="traces-empty">{traces.error ?? 'Loading…'}</div>
      )}
    </Frame>
  );
}

// Neurons, a circuit (a microcircuit, or a two-layer column): its spikes as a raster by cell type, each
// type's rows under the population rates. A cell's row holds its spikes; the Cells checkboxes leave a type
// out. A column's eight types keep their layer in the label (data/sims/circuit.ts).

/** Height for the rate lane and the rasters: sized for four types, a row's worth more for each beyond (a column's eight). */
const circuitHeight = (types: number) => HEIGHT + 56 + 26 * Math.max(0, types - 4);

/** A raster lane's weight: its share of the plot grows with its cell count's square root, so a 50-cell type still reads beside 800. */
const rasterWeight = (n: number, most: number) => 1.6 * Math.max(0.25, Math.sqrt(n / Math.max(1, most)));

function CircuitTraces() {
  const o = openedSlot('main');
  const sim = o?.format === 'cells' ? (o.sim as CellsSim) : null;
  const setTime = useAtlas((s) => s.setTime);
  const hidden = useAtlas((s) => s.traceView.hiddenTypes);
  const circuit = useLoaded(() => (sim ? loadCircuit(sim) : null), [sim]);
  const model = useMemo((): ChartModel | null => {
    const c = circuit.value;
    if (!c) return null;
    const shown = c.groups.filter((g) => !hidden?.includes(g.type));
    const most = Math.max(...c.groups.map((g) => g.cells.length));
    const bins = Float64Array.from({ length: c.groups[0]?.rate.length ?? 0 }, (_, b) => c.t0 + Math.min(c.seconds, (b + 0.5) * c.binS));
    const rate: Lane = { label: 'Rate', unit: 'Hz', y: [0, rateTop(shown.map((g) => g.rate))], ticks: 'auto', weight: 1.1, series: shown.map((g) => ({ label: g.label, colour: g.colour, x: bins, y: g.rate, width: 1.25, unit: 'Hz' })) };
    const rows: Lane[] = shown.map((g) => ({
      label: g.label,
      y: [0, g.cells.length],
      ticks: 'none',
      weight: rasterWeight(g.cells.length, most),
      series: [{ label: g.label, colour: g.colour, x: g.times, y: g.rows, style: 'ticks', width: 1.5, readout: false }],
    }));
    // The gutter fits the longest row label: a column's rows carry their layer (L2/3 Pyramidal), which a fixed 72 px cut off the left.
    return { x: { domain: [c.t0, c.t0 + c.seconds], format: c.seconds >= 1 ? secFormat : msFormat }, lanes: [rate, ...rows], gutter: rasterGutter(shown.map((g) => g.label), (s) => textWidth(s, MONO)) };
  }, [circuit.value, hidden]);
  if (!sim) return null;
  const c = circuit.value;
  const title = typeof sim.header.model?.name === 'string' ? sim.header.model.name : `${formatCount(sim.cells.length)} cells`;
  return (
    <Frame
      title={title}
      sub={`${formatCount(sim.cells.length)} cells${c && c.layers.length > 1 ? ` in ${c.layers.length} layers` : ''}, spikes by ${c && c.layers.length > 1 ? 'layer and type' : 'type'} · ${c && c.seconds < 1 ? 'ms' : 's'}`}
      keyBar={c ? <SwatchKey items={c.groups.filter((g) => !hidden?.includes(g.type)).map((g) => ({ label: g.label, colour: g.colour }))} /> : null}
      wrap={!!c && c.groups.length > 4}
    >
      {model ? (
        <Chart label="The circuit’s spikes by cell type, each cell a row, under each type’s population rate" model={model} height={circuitHeight(c?.groups.length ?? 4)} cursor={sceneTime} onSeek={(x) => setTime(x)} />
      ) : (
        <div className="traces-empty">{circuit.error ?? 'Loading…'}</div>
      )}
    </Frame>
  );
}

export function Traces() {
  const slots = useAtlas(slotsOf);
  const live = useAtlas(liveActive);
  // A still view (Drugs' measured ΔFC, features/Dfc.tsx) has no course to trace.
  const still = useAtlas(stillView);
  // Lifetime's Cortical thickness view (features/Lifetime.tsx): the reference's mean by age with the chosen ages marked, no clock.
  const thickness = useAtlas(thicknessView);
  const main = slots.find((x) => x.slot === 'main');
  if (still) return thickness ? <ThicknessTraces /> : null;
  // A clot where hemo has no run still has the live solve.
  if (!main) return live && liveSolver() ? <VesselTraces follows={false} /> : null;
  switch (main.format) {
    case 'hemo':
      return main.header.kind === '1d-network' ? <VesselTraces slot={main} follows /> : main.header.files && 'growth' in main.header.files ? <StrokeTraces main={main} /> : null;
    case 'dynamics':
      // Mind's retinotopy run: the visual field the cortex is shown, not electrodes (features/VisualField.tsx).
      if (main.path.startsWith('mind/')) return <VisualFieldStrip main={main} />;
      return isPatientComparison(main.header) ? <PatientTraces /> : <EegTraces />;
    case 'pharma':
      return <DrugTraces />;
    case 'cells':
      return isCircuitKind(main.header.kind) ? <CircuitTraces /> : <CellTraces />;
    case 'stim':
      // A static field: nothing over time to trace.
      return null;
    case 'lifetime':
      // The atlas's own growth: its templates' area and volume by gestational week (features/Lifetime.tsx).
      return <LifetimeTraces main={main} />;
  }
}
